import { createFixture, fixturePresetFromSearch } from "./fixture";
import type {
  ActivationPreview,
  ActivationRedemption,
  BoardEditRequest,
  CommandRequest,
  ForgeRemoteApi,
  ManagerMessageRequest,
  OperationReceipt,
  ProjectionEnvelope,
  ProjectionStreamOptions,
} from "./contracts";

const delay = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export class FixtureForgeRemoteApi implements ForgeRemoteApi {
  readonly sourceKind = "development-fixture" as const;
  readonly supportsProjectionStream = false;
  readonly #fixture;

  constructor(search: string) {
    this.#fixture = createFixture(fixturePresetFromSearch(search));
  }

  hasCommandProtection() {
    return false;
  }

  async getActivationPreview(): Promise<ActivationPreview> {
    await delay(120);
    return {
      schemaVersion: "forge-remote.activation-preview.v1",
      activationId: "development-fixture-activation",
      hostLabel: this.#fixture.session.hostLabel,
      expiresAt: this.#fixture.session.expiresAt,
      capabilities: [...this.#fixture.session.capabilities],
      crews: this.#fixture.crews.map((crew) => ({ id: crew.id, name: crew.name, state: crew.runState })),
      source: "fixture-preview",
      commandAuthority: "unavailable",
    };
  }

  async redeemActivation(): Promise<ActivationRedemption> {
    await delay(320);
    return {
      schemaVersion: "forge-remote.activation-redemption.v1",
      sessionId: this.#fixture.session.sessionId,
      expiresAt: this.#fixture.session.expiresAt,
    };
  }

  async getCurrentProjection(): Promise<ProjectionEnvelope> {
    await delay(100);
    return {
      schemaVersion: "forge-remote.projection.v3",
      cursor: this.#fixture.session.projection.cursor,
      authorityEpoch: 1,
      cursorRevision: 1,
      cursorReset: false,
      historyTruncated: false,
      projection: structuredClone(this.#fixture),
    };
  }

  async streamProjectionUpdates(options: ProjectionStreamOptions): Promise<void> {
    void options;
  }

  async endSession(): Promise<void> {
    await delay(80);
  }

  async sendManagerMessage(request: ManagerMessageRequest): Promise<OperationReceipt> {
    await delay(240);
    void request;
    throw new Error("Development fixture has no authoritative Manager message channel.");
  }

  async submitCommand(request: CommandRequest): Promise<OperationReceipt> {
    await delay(360);
    void request;
    throw new Error("Development fixture has no authoritative command channel.");
  }

  async editBoardItem(request: BoardEditRequest): Promise<OperationReceipt> {
    await delay(360);
    void request;
    throw new Error("Development fixture has no authoritative board command channel.");
  }
}
